import { Box, List, ListItem, makeStyles, Theme, Typography, Button } from '@material-ui/core';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import React, { useRef, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { member as v_member, basic, Values, product as v_product, SNACKBAR_TIME } from '../../../request/values';
import ToastStr from '../../../request/toastStr';
import { DialogInputConfirm, DialogPopup, DialogSelectCompany } from '../../../components/Dialog';
import { COLORS } from '../../../shared/Theme';
import { TitleBarWithButton } from '../../../shared/header/TitleBar';
import { formatMoneyAmount, getTranslatedLicenseName } from '../../../locales/formatModule';
import { getSavedLanguage } from '../../../locales';

const SERVER = basic.server;

const useStyles = makeStyles((theme: Theme) => ({
	list: {
		padding: 0,
		width: '100%',
		height: '78vh',
		overflowY: 'auto',
		fontFamily: 'NanumB',
		fontWeight: 'bold',
		fontSize: '4vw',
		color: `${COLORS.BROWNISH_GREY}`,
		[theme.breakpoints.up('md')]: {
			height: '100%',
			fontSize: 'initial',
		},
	},
	productItem: {
		borderRadius: 5,
		border: 'solid 1px #d3d3d3',
		boxShadow: 'none',
		marginBottom: '1.7vh',
		width: '100%',
		margin: 0,
		padding: 16,
		backgroundColor: COLORS.WHITE,
		boxSizing: 'border-box',
		'& #itemWrap': {
			width: '100%',
		},
		[theme.breakpoints.up('md')]: {
			padding: 20,
		},
	},

	productImage: {
		textAlign: 'center',
		margin: 'auto',
		'& img': {
			// width: '18vw',
			height: '6.5vh',
		},
	},
	button: {
		borderRadius: 5,
		width: '30%',
		height: '4vh',
		padding: 0,
		backgroundColor: `${COLORS.WATER_BLUE}`,
		color: `${COLORS.WHITE}`,

		...theme.typography.body1,
		fontFamily: 'NanumB',
		fontWeight: 'bold',
		[theme.breakpoints.up('md')]: {
			padding: '25px 0',
			...theme.typography.body1,
			fontFamily: 'NanumB',
			fontWeight: 'bold',
		},
	},
	button2: {
		borderRadius: 5,
		width: '25vw',
		height: '4vh',
		marginBottom: '1.7vh',
		padding: 0,
		backgroundColor: `${COLORS.WATER_BLUE}`,
		color: `${COLORS.WHITE}`,
		...theme.typography.body1,
		fontFamily: 'NanumB',
		fontWeight: 'bold',
	},
	productDesc: {
		//paddingLeft: '5vw',
		textAlign: 'center',
		flexGrow: 1,
		[theme.breakpoints.up('md')]: {
			paddingLeft: '0px',
		},
		'& #productName': {
			whiteSpace: 'nowrap',
			textOverflow: 'ellipsis',
			...theme.typography.body1,
			color: `${COLORS.BROWNISH_GREY}`,
		},
		'& #productPrice': {
			...theme.typography.subtitle2,
			color: `${COLORS.CHARCOAL_GREY}`,
			fontFamily: 'NanumB',
			fontWeight: 'bold',
		},
	},
	paper: {
		padding: theme.spacing(1),
	},
	grid: {
		marginTop: theme.spacing(1),
	},

	empty: {
		textAlign: 'center',
		marginTop: '30%',
		[theme.breakpoints.up('md')]: {
			marginTop: '10%',
		},
	},

	empty_image: {
		width: '45vw',
		[theme.breakpoints.up('md')]: {
			width: '25vw',
		},
	},

	productCard: {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',

		'& #cardTop': {
			display: 'flex',
			flexDirection: 'row',
			alignItems: 'center',
			gap: 14,
		},

		'& #imgContainer': {
			flexShrink: 0,
			width: 80,
			height: 80,
			borderRadius: 12,
			backgroundColor: COLORS.WHITE,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			overflow: 'hidden',
			'& #productImg': {
				maxWidth: '100%',
				maxHeight: '100%',
				width: 'auto',
				height: 'auto',
				objectFit: 'contain',
			},
		},

		'& #productInfo': {
			flex: 1,
			minWidth: 0,
			display: 'flex',
			flexDirection: 'column',
			gap: 8,
			'& #licenseInfo': {
				display: 'flex',
				justifyContent: 'space-between',
				alignItems: 'flex-start',
				gap: 8,
				'& #licenseName': {
					flex: 1,
					minWidth: 0,
					color: COLORS.WATER_BLUE,
					fontSize: '1.1rem',
					fontWeight: 'bold',
					lineHeight: 1.3,
				},
				'& #licenseLevel': {
					flexShrink: 0,
					borderRadius: 10,
					padding: '4px 8px',
					background: '#C2E9FF',
					color: COLORS.WATER_BLUE,
					fontSize: '0.85rem',
					fontWeight: 'bold',
					lineHeight: 1,
				},
			},
			'& #productStatus': {
				display: 'flex',
				flexDirection: 'column',
				gap: 6,
				'& #priceLine, & #completeLine': {
					display: 'flex',
					alignItems: 'baseline',
					gap: 8,
				},
				'& #priceLabel, & #completeLabel': {
					flexShrink: 0,
					minWidth: 68,
					color: '#90A4B8',
					fontWeight: 700,
					fontSize: '0.9rem',
				},
				'& #priceValue, & #completeValue': {
					fontSize: '0.9rem',
					fontWeight: 700,
					whiteSpace: 'nowrap',
				},
				'& #priceValue': {
					color: COLORS.WATER_BLUE,
				},
				'& #completeValue': {
					color: '#2DBE60',
				},
			},
			'& #materialStatus1, & #materialStatus2, & #materialStatus3, & #materialStatus4': {
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				height: '90px',
				padding: '8px 10px',
				borderRadius: '5px',
				fontSize: '1rem',
				fontWeight: 'bold',
			},
			'& #materialStatus1': {
				border: '1px solid #1AB82D',
				background: '#D6FBCE',
				color: '#1AB82D',
			},
			'& #materialStatus2': {
				//border: '1px solid #F14242',
				background: '#FBCECE',
				color: '#F14242',
			},
			'& #materialStatus3': {
				display: 'flex',
				flexDirection: 'column',
				//border: '1px solid #F14242',
				background: '#FBCECE',
				color: '#F14242',
				alignItems: 'flex-start',
				fontSize: '0.7rem',
				'& #insufficientMaterial': {
					width: '100%',
					overflowY: 'auto',
					display: 'flex',
					flexDirection: 'column',
					marginTop: '5px',
				},
			},
			'& #materialStatus4': {
				//border: `1px solid ${COLORS.WATER_BLUE}`,
				background: '#C2E9FF',
				color: COLORS.WATER_BLUE,
			},
			'& #productButtonContainer': {
				display: 'flex',
				justifyContent: 'space-between',
				gap: '5px',

				'& #doubleButton': {
					width: '100%',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					borderRadius: '5px',
					fontWeight: 'bold',
					color: 'white',
					border: 'none',
					padding: '5px',
					background: COLORS.WATER_BLUE,
					'& #titleText': {
						fontSize: '0.8rem',
					},
					'& #subtitleText': {
						fontSize: '0.65rem',
					},
				},
			},
		},
		'& #cardDivider': {
			height: 1,
			backgroundColor: '#e4e4e4',
			margin: '12px 0',
			width: '100%',
		},
		'& #cardBottom': {
			width: '100%',
		},
		'& #buttonOn, & #buttonOff, & #buttonSelling': {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			borderRadius: 8,
			fontFamily: 'NanumB',
			fontSize: '0.95rem',
			fontWeight: 'bold',
			border: 'none',
			padding: '2vw',
			width: '100%',
			height: 'auto',
			textTransform: 'none',
			boxShadow: 'none',
			[theme.breakpoints.up('md')]: {
				padding: '1vw',
			},
		},
		'& #buttonOn': {
			background: COLORS.WATER_BLUE,
			color: COLORS.WHITE,
		},
		'& #buttonSelling': {
			background: '#EAF3FB',
			color: COLORS.WATER_BLUE,
		},
		'& #buttonOff': {
			background: '#BDBDBD',
			color: '#fff',
		},
	},
}));

interface iUnEff {
	materialCode: number;
	materialName: string;
	materialSubName: string;
	materialImage: string;
	materialPrice: number;
	materialCount: number;
}

interface iCompanyInfo {
	companyNum: number;
	companyName: string;
}

const ProductList: React.FC<any> = (props) => {
	const classes = useStyles();
	const history = useHistory();
	const { member } = props;
	const { t } = useTranslation('student');
	const { t: tObject } = useTranslation('object');
	const [product, setProduct] = useState<any>([]);
	const [dialog, setDialog] = useState(-1);
	const [loading, setLoading] = useState(false);
	const [successSell, setSuccessSell] = useState(false);
	const [companyList, setCompanyList] = useState<iCompanyInfo[]>([]);
	const [reset, setReset] = useState<boolean>(false);
	const isEnglish = getSavedLanguage() === 'en';
	const { enqueueSnackbar, closeSnackbar } = useSnackbar();
	const timer = useRef<number | 0>(0);

	const resetStates = () => {
		setReset(true); // 초기화 트리거
		setTimeout(() => setReset(false), 0); // 초기화 후 다시 초기화 상태를 false로
	};

	const AddComma = (num: number) => {
		var regexp = /\B(?=(\d{3})+(?!\d))/g;
		return num.toString().replace(regexp, ',');
	};

	const handleDialogOpen = (index: number) => {
		if (dialog == -1) {
			setDialog(index);
		}
	};

	const handleDialogClose = () => {
		setDialog((prevState) => -1);
	};

	const handleNoClose = () => {};

	const successDialogOpen = () => {
		setSuccessSell(true);
		setLoading(false);

		console.log(timer.current);
		if (timer.current != 0) {
			window.clearTimeout(timer.current);
		}
		timer.current = window.setTimeout(() => {
			setSuccessSell(false);
			resetStates();
			getProductionList();
		}, 3000);
	};

	const getDialogContext = () => {
		const licenseName = getTranslatedLicenseName(tObject, product[dialog].productName);
		const tProduct: any = product[dialog];
		const minPrice: number = tProduct.productPrice * (9 / 10);
		const maxPrice: number = tProduct.productPrice * (11 / 10);
		if (member.classVersion === 4 || member.classVersion === 5)
			return `${t('ProductList.dialogContextMinMax', { min: formatMoneyAmount(Math.floor(minPrice)), max: formatMoneyAmount(Math.floor(maxPrice)) })}`;
		else return `${t('ProductList.dialogContextDefault', { level: tProduct.productLicenseLevel, name: licenseName, price: formatMoneyAmount(tProduct.productPrice) })}`;
	};

	const getCompanyList = () => {
		let url = new URL(basic.server + v_member.member + v_member.decompose_company_list);
		let params = { classCode: member.classCode };
		url.search = new URLSearchParams(params).toString();

		axios({
			method: 'GET',
			withCredentials: true,
			url: url.toString(),
		})
			.then((res) => res.data)
			.then((data: any) => {
				if (data.code == Values.SUCCESS_CODE) {
					setCompanyList(data.companyList);
					console.log('company list? ', data.companyList);
				}
			});
	};

	const getProductionList = () => {
		let url = new URL(SERVER + v_member.member + v_product.sale_product_list);
		let params = { companyNum: member.companyNum.toString(), classVersion: member.classVersion };
		url.search = new URLSearchParams(params).toString();

		axios({
			method: 'GET',
			withCredentials: true,
			url: url.toString(),
		})
			.then((res) => res.data)
			.then((data: any) => {
				console.log(data);
				if (data.code == Values.SUCCESS_CODE) {
					//setProduct((prevState: any) => data.products.reverse());
					setProduct((prevState: any) => data.products);
					setLoading((prevState) => true);
				}
			});
	};

	const postProductSaleApplication = (companyNum: number) => {
		const targetProduct: any = product[dialog];
		if (companyNum === undefined || companyNum === 0) {
			enqueueSnackbar(ToastStr.PRODUCT_SALE_APP_NO_COMPANY_STR, {
				variant: 'warning',
				autoHideDuration: SNACKBAR_TIME,
			});
			return;
		}
		axios({
			method: 'POST',
			data: {
				companyNum: member.companyNum.toString(),
				memberName: member.name.toString(),
				productNum: targetProduct.productNum.toString(),
				decomposeCompanyNum: companyNum,
			},
			withCredentials: true,
			url: basic.server + v_member.member + v_product.sale_application,
		})
			.then((res) => res.data)
			.then((data: any) => {
				console.log(data);
				if (data.code == Values.SUCCESS_CODE) {
					successDialogOpen();
					enqueueSnackbar(ToastStr.PRODUCT_SALE_APP_SUCCESS, {
						variant: 'success',
						autoHideDuration: SNACKBAR_TIME,
					});
				} else if (data.code == Values.SALE_ING_CODE) {
					enqueueSnackbar(ToastStr.PRODUCT_SALE_APP_FINISH_FAIL, {
						variant: 'warning',
						autoHideDuration: SNACKBAR_TIME,
					});
					getProductionList();
				}
			});
		handleDialogClose();
	};

	const postProductSaleApplication2 = (price: number, companyNum: number) => {
		const minPrice: number = Math.floor(product[dialog].productPrice * (9 / 10));
		const maxPrice: number = Math.floor(product[dialog].productPrice * (11 / 10));
		if (price < minPrice || price > maxPrice) {
			enqueueSnackbar(ToastStr.PRODUCT_PRICE_WRONG_STR, {
				variant: 'warning',
				autoHideDuration: SNACKBAR_TIME,
			});
			return;
		}
		if (companyNum === undefined || companyNum === 0) {
			enqueueSnackbar(ToastStr.PRODUCT_SALE_APP_NO_COMPANY_STR, {
				variant: 'warning',
				autoHideDuration: SNACKBAR_TIME,
			});
			return;
		}
		const targetProduct: any = product[dialog];
		axios({
			method: 'POST',
			data: {
				companyNum: member.companyNum.toString(),
				memberName: member.name.toString(),
				productNum: targetProduct.productNum.toString(),
				price: price.toString(),
				decomposeCompanyNum: companyNum,
			},
			withCredentials: true,
			url: basic.server + v_member.member + v_product.sale_application_price,
		})
			.then((res) => res.data)
			.then((data: any) => {
				console.log(data);
				if (data.code == Values.SUCCESS_CODE) {
					successDialogOpen();
					enqueueSnackbar(ToastStr.PRODUCT_SALE_APP_SUCCESS, {
						variant: 'success',
						autoHideDuration: SNACKBAR_TIME,
					});
				} else if (data.code == Values.SALE_ING_CODE) {
					enqueueSnackbar(ToastStr.PRODUCT_SALE_APP_FINISH_FAIL, {
						variant: 'warning',
						autoHideDuration: SNACKBAR_TIME,
					});
					getProductionList();
				}
			});
		handleDialogClose();
	};

	const goSaleList = () => {
		history.push({
			pathname: '/ProductSaleList/',
			state: {
				// location state
			},
		});
	};

	useEffect(() => {
		const fetchData = async () => {
			await getProductionList();
			await getCompanyList();
		};
		fetchData();
	}, []);

	return (
		<div className="contentSmallPdTop">
			<TitleBarWithButton
				history={history}
				title={t('ProductList.pageTitle')}
				titleIcon="product"
				buttonCheck={true}
				buttonText={t('ProductList.goReceipt')}
				buttonOnClick={goSaleList}
				buttonTestId="manu-product-go-receipt-btn"
			/>
			<Box display="flex" justifyContent="center" alignItems="center">
				<List className={classes.list}>
					{loading == true ? (
						product.length != 0 ? (
							product.map((item: any, index: number) => {
								return (
									<ListItem className={classes.productItem} alignItems="center">
										<ProductItem
											index={index}
											{...item}
											handleButton={() => handleDialogOpen(index)}
											AddComma={AddComma}
											loading={true}
											companyNum={member.companyNum}
											classVersion={member.classVersion}
											reset={reset}
										/>
									</ListItem>
								);
							})
						) : (
							<div className={classes.empty}>
								<img className={classes.empty_image} src={isEnglish ? '/images/no_product_en.webp' : '/images/no_product.webp'} />
								<Typography
									variant="h1"
									style={{
										color: `${COLORS.DOWN}`,
										marginTop: '1.5vh',
									}}
								>
									{t('ProductList.empty')}
								</Typography>
							</div>
						)
					) : (
						Array.from(Array(5), (e, i) => {
							return (
								<ListItem className={classes.productItem} alignItems="center">
									<ProductItem loading={false} />
								</ListItem>
							);
						})
					)}
				</List>
			</Box>
			{dialog >= 0 &&
				(member.classVersion === 4 || member.classVersion === 5 ? (
					<DialogInputConfirm
						open={dialog < 0 ? false : true}
						handleClose={() => handleDialogClose()}
						title={t('ProductList.selectCompanyAndPrice')}
						//context ={(10 - timer.current) + "하이"}
						companyList={companyList}
						price={product[dialog].productPrice}
						context={getDialogContext()}
						yesConfirm={postProductSaleApplication2}
						noConfirm={() => handleDialogClose()}
					/>
				) : (
					<DialogSelectCompany
						open={dialog < 0 ? false : true}
						handleClose={() => handleDialogClose()}
						title={t('ProductList.selectCompany')}
						companyList={companyList}
						//context={getDialogContext()}
						context={getDialogContext()}
						yesConfirm={postProductSaleApplication}
						//yesConfirm={postMaterialOrder}
						noConfirm={handleDialogClose}
					/>
				))}
			{successSell && (
				<DialogPopup open={successSell == true ? true : false} handleClose={() => handleNoClose()} title={t('ProductList.successTitle')} context={t('ProductList.successContext')} />
			)}
		</div>
	);
};

const ProductItem: React.FC<any> = (props) => {
	const classes = useStyles();
	const { t } = useTranslation('student');
	const { t: tObject } = useTranslation('object');
	const licenseName = getTranslatedLicenseName(tObject, props.productName);
	// materialStatus: null=판매 불가, 1=판매가능, 2=판매중
	const canSell = props.materialStatus === 1;
	const isSelling = props.materialStatus === 2;

	if (props.loading) {
		return (
			<div id="itemWrap" data-testid={`manu-product-item-${props.licenseNum}`}>
				<div className={classes.productCard}>
					<div id="cardTop">
						<div id="imgContainer">
							<img id="productImg" src={basic.server + '/displayImage?fileName=' + props.productImage} alt={licenseName} />
						</div>
						<div id="productInfo">
							<div id="licenseInfo">
								<div id="licenseName">{licenseName}</div>
								<div id="licenseLevel">{`Lv. ${props.productLicenseLevel}`}</div>
							</div>
							<div id="productStatus">
								<div id="priceLine">
									<div id="priceLabel">{t('ProductList.labelPrice')}</div>
									<div id="priceValue">{formatMoneyAmount(props.productPrice)}</div>
								</div>
								<div id="completeLine">
									<div id="completeLabel">{t('ProductList.labelComplete')}</div>
									<div id="completeValue">{`${Number(props.completeNum) || 0}${t('ProductList.countSuffix')}`}</div>
								</div>
							</div>
						</div>
					</div>
					<div id="cardDivider" />
					<div id="cardBottom">
						<Button
							fullWidth
							variant="contained"
							id={canSell ? 'buttonOn' : isSelling ? 'buttonSelling' : 'buttonOff'}
							disabled={!canSell}
							onClick={props.handleButton}
							data-testid={`manu-product-sell-btn-${props.licenseNum}`}
						>
							{canSell ? t('ProductList.sellProduct') : isSelling ? t('ProductList.selling') : t('ProductList.materialShortage')}
						</Button>
					</div>
				</div>
			</div>
		);
	} else {
		return (
			<div id="itemWrap">
				<div className={classes.productCard}>
					<div id="cardTop">
						<Skeleton animation="wave" variant="rectangular" width={80} height={80} style={{ borderRadius: 12, flexShrink: 0 }} />
						<div id="productInfo" style={{ flex: 1 }}>
							<Skeleton animation="wave" width="80%" height={24} />
							<Skeleton animation="wave" width="60%" height={20} />
							<Skeleton animation="wave" width="40%" height={16} />
						</div>
					</div>
					<div id="cardDivider" />
					<div id="cardBottom">
						<Skeleton animation="wave" variant="rectangular" width="100%" height={40} style={{ borderRadius: 8 }} />
					</div>
				</div>
			</div>
		);
	}
};

export default ProductList;
